import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { todosApi } from '../api/clients';
import { toApiError } from '../api/problem';
import { PriorityBadge, ProgressBadge } from '../components/Badges';
import { TargetTimeFields } from '../components/TargetTimeFields';
import { TaskChecklist } from '../components/TaskChecklist';
import { useToast } from '../components/Toasts';
import { ChevronLeftIcon, ClockIcon } from '../components/icons';
import { useDragReorder } from '../components/useDragReorder';
import { useI18n } from '../i18n/I18nProvider';
import { formatDayLong, formatInstant, isOverdue } from '../utils/date';
import { PRIORITY_LABEL_KEY, PRIORITY_ORDER } from '../utils/priority';
import {
  AddTaskCommand,
  MoveTodoRequest,
  PriorityLevel,
  ReorderTasksRequest,
  RestartTaskRequest,
  SetTaskCommentRequest,
  SetTaskResultRequest,
  TargetTimeInput,
  TargetTimeMode,
  TodoDetailDto,
  TodoProgressKind,
  TodoTaskDto,
  TodoTaskStage,
  UpdateTaskCommand,
  UpdateTodoCommand
} from '../web-api-client';

/** Đổi riêng vòng đời (status) của một task trong bản todo đang giữ trên màn hình. */
function patchTaskStage(current: TodoDetailDto | null, index: number, stage: TodoTaskStage) {
  return current
    ? new TodoDetailDto({
        ...current,
        todoTask: (current.todoTask ?? []).map(t => (t.index === index ? new TodoTaskDto({ ...t, status: stage }) : t))
      })
    : current;
}

export function TodoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const { showToast } = useToast();

  const [todo, setTodo] = useState<TodoDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const reload = useCallback(async () => {
    if (!id) return;

    setLoadError(null);

    try {
      setTodo(await todosApi.getTodoDetail(id));
    } catch (caught) {
      setLoadError(toApiError(caught).message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    reload();
  }, [reload]);

  const fail = useCallback(
    (caught: unknown) => showToast(toApiError(caught).message, { tone: 'danger' }),
    [showToast]
  );

  /**
   * Đổi kết quả là thao tác được bấm nhiều nhất, nên nó cập nhật ngay trên màn hình rồi mới gọi máy
   * chủ. Nhưng bảng tổng kết thì luôn phải lấy lại từ máy chủ: nó là kết quả của một luật đếm có cả
   * quy tắc hòa, và nếu giao diện tự đoán thì sẽ có lúc đoán sai.
   *
   * Ô tick vẫn hoạt động độc lập với huy hiệu vòng đời: nếu task còn New, tự gọi StartTask trước để
   * click một cái vẫn ra kết quả ngay, không bắt người dùng phải bấm "Bắt đầu" riêng trước.
   */
  const setStatus = async (index: number, status: string) => {
    const previous = todo;
    const task = (todo?.todoTask ?? []).find(t => t.index === index);

    setTodo(current =>
      current
        ? new TodoDetailDto({
            ...current,
            todoTask: (current.todoTask ?? []).map(t =>
              t.index === index ? new TodoTaskDto({ ...t, result: status }) : t
            )
          })
        : current
    );

    try {
      if (task?.status === TodoTaskStage.New) {
        await todosApi.startTask(id!, index);
      }

      await todosApi.setTaskResult(id!, index, new SetTaskResultRequest({ result: status }));
      await reload();
    } catch (caught) {
      setTodo(previous);
      fail(caught);
    }
  };

  /** Bấm huy hiệu/bước "Bắt đầu" — New → InProcess. */
  const startTask = async (index: number) => {
    const previous = todo;

    setTodo(current => patchTaskStage(current, index, TodoTaskStage.InProcess));

    try {
      await todosApi.startTask(id!, index);
      await reload();
    } catch (caught) {
      setTodo(previous);
      fail(caught);
    }
  };

  /** Bấm huy hiệu/bước "Xong" — InProcess → End. Máy chủ tự chặn nếu chưa có kết quả. */
  const finishTask = async (index: number) => {
    const previous = todo;

    setTodo(current => patchTaskStage(current, index, TodoTaskStage.End));

    try {
      await todosApi.finishTask(id!, index);
      await reload();
    } catch (caught) {
      setTodo(previous);
      fail(caught);
    }
  };

  /** Bắt đầu lại kèm lý do — quay về New, xóa kết quả cũ. Trả về có thành công không để popover biết. */
  const restartTask = async (index: number, reason: string): Promise<boolean> => {
    try {
      await todosApi.restartTask(id!, index, new RestartTaskRequest({ reason }));
      await reload();
      return true;
    } catch (caught) {
      fail(caught);
      return false;
    }
  };

  const saveTask = async (task: TodoTaskDto, changes: Partial<TodoTaskDto>) => {
    try {
      await todosApi.updateTask(
        id!,
        task.index!,
        new UpdateTaskCommand({
          content: changes.content ?? task.content,
          priority: changes.priority ?? task.priority,
          estimateTime: changes.estimateTime ?? task.estimateTime,
          comment: 'comment' in changes ? changes.comment : task.comment
        })
      );

      await reload();
    } catch (caught) {
      fail(caught);
      await reload();
    }
  };

  const saveComment = async (index: number, comment: string) => {
    try {
      await todosApi.setTaskComment(id!, index, new SetTaskCommentRequest({ comment: comment || undefined }));
      await reload();
    } catch (caught) {
      fail(caught);
    }
  };

  const addTask = async (content: string) => {
    try {
      await todosApi.addTask(
        id!,
        new AddTaskCommand({
          content,
          priority: PriorityLevel.None,
          estimateTime: 0
        })
      );

      await reload();
    } catch (caught) {
      fail(caught);
    }
  };

  const removeTask = async (task: TodoTaskDto) => {
    try {
      await todosApi.removeTask(id!, task.index!);
      await reload();
      showToast(t('detail.taskDeleted', { content: task.content ?? '' }));
    } catch (caught) {
      fail(caught);
    }
  };

  const commitOrder = useCallback(
    async (ordered: TodoTaskDto[]) => {
      const previous = todo;

      setTodo(current => (current ? new TodoDetailDto({ ...current, todoTask: ordered }) : current));

      try {
        await todosApi.reorderTasks(id!, new ReorderTasksRequest({ newOrder: ordered.map(task => task.index!) }));
        await reload();
      } catch (caught) {
        setTodo(previous);
        fail(caught);
      }
    },
    [id, todo, reload, fail]
  );

  const tasks = todo?.todoTask ?? [];
  const drag = useDragReorder(tasks, commitOrder);

  const removeTodo = async () => {
    try {
      const snapshot = await todosApi.deleteTodo(id!);

      navigate(`/day/${snapshot.todoDate!}`, { replace: true });

      showToast(t('todo.deleted', { title: snapshot.title ?? '' }), {
        durationMs: 5000,
        action: {
          label: t('todo.undo'),
          onClick: async () => {
            try {
              const restoredId = await todosApi.restoreTodo(snapshot);
              navigate(`/todo/${restoredId}`);
            } catch (caught) {
              fail(caught);
            }
          }
        }
      });
    } catch (caught) {
      fail(caught);
    }
  };

  if (loading) return <p className="text-body-secondary">{t('common.loading')}</p>;

  if (loadError) {
    return (
      <div className="alert alert-warning">
        <p className="mb-2">{loadError}</p>
        <Link className="btn btn-sm btn-outline-secondary" to="/">
          {t('common.backToToday')}
        </Link>
      </div>
    );
  }

  if (!todo) return null;

  const done = todo.status?.overall === TodoProgressKind.Completed;
  const overdue = isOverdue(todo.targetTime) && !done;

  return (
    <>
      <section className="panel detail-header">
        <div>
          <Link className="detail-back" to={`/day/${todo.todoDate}`}>
            <ChevronLeftIcon size={16} />
            {formatDayLong(todo.todoDate!, lang)}
          </Link>

          <h1 className="detail-title">{todo.title}</h1>

          <div className="d-flex gap-2 align-items-center flex-wrap">
            <ProgressBadge kind={todo.status?.overall} />
            <PriorityBadge priority={todo.priority} />

            {todo.targetTime && (
              <span className={`target-chip ${overdue ? 'is-overdue' : ''}`}>
                <ClockIcon />
                {t('todo.target', { time: formatInstant(todo.targetTime) })}
              </span>
            )}

            {todo.refTable && (
              <span className="tag tag-idle">
                {t('detail.linked', { table: todo.refTable, id: todo.refId ?? '' })}
              </span>
            )}
          </div>
        </div>

        <div className="d-flex gap-2 flex-shrink-0">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setEditing(value => !value)}
          >
            {editing ? t('detail.close') : t('detail.edit')}
          </button>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={removeTodo}>
            {t('detail.deleteTodo')}
          </button>
        </div>
      </section>

      {editing && (
        <TodoEditor
          todo={todo}
          onSaved={async () => {
            setEditing(false);
            await reload();
          }}
          onError={fail}
        />
      )}

      <TaskChecklist
        tasks={drag.items}
        status={todo.status}
        draggingIndex={drag.draggingIndex}
        itemProps={drag.itemProps}
        onAdd={addTask}
        onStatus={setStatus}
        onStart={startTask}
        onFinish={finishTask}
        onRestart={restartTask}
        onSave={saveTask}
        onComment={saveComment}
        onRemove={removeTask}
      />
    </>
  );
}


function TodoEditor({
  todo,
  onSaved,
  onError
}: {
  todo: TodoDetailDto;
  onSaved: () => void;
  onError: (caught: unknown) => void;
}) {
  const { t } = useI18n();

  const [title, setTitle] = useState(todo.title ?? '');
  const [priority, setPriority] = useState<PriorityLevel>(todo.priority ?? PriorityLevel.None);
  const [day, setDay] = useState(todo.todoDate ?? '');
  const [targetTime, setTargetTime] = useState<TargetTimeInput>(
    new TargetTimeInput(
      todo.targetTime ? { mode: TargetTimeMode.ExactDateTime, at: todo.targetTime } : { mode: TargetTimeMode.None }
    )
  );
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);

    try {
      await todosApi.updateTodo(
        todo.id!,
        new UpdateTodoCommand({
          title: title.trim(),
          priority,
          targetTime: targetTime.mode === TargetTimeMode.None ? undefined : targetTime,
          refTable: todo.refTable,
          refId: todo.refId
        })
      );

      // Chuyển ngày là một lệnh riêng, chỉ gọi khi ngày thật sự đổi.
      if (day && day !== todo.todoDate) {
        await todosApi.moveTodoToDay(todo.id!, new MoveTodoRequest({ todoDate: day }));
      }

      onSaved();
    } catch (caught) {
      onError(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="todo-editor" onSubmit={submit}>
      <div className="row g-2">
        <div className="col-sm-6">
          <label className="form-label small text-body-secondary">{t('detail.title')}</label>
          <input
            className="form-control"
            value={title}
            maxLength={200}
            onChange={event => setTitle(event.target.value)}
            required
          />
        </div>
        <div className="col-sm-3">
          <label className="form-label small text-body-secondary">{t('priority.label')}</label>
          <select
            className="form-select"
            value={priority}
            onChange={event => setPriority(event.target.value as PriorityLevel)}
          >
            {PRIORITY_ORDER.map(level => (
              <option key={level} value={level}>
                {t(PRIORITY_LABEL_KEY[level])}
              </option>
            ))}
          </select>
        </div>
        <div className="col-sm-3">
          <label className="form-label small text-body-secondary">{t('detail.day')}</label>
          <input type="date" className="form-control" value={day} onChange={event => setDay(event.target.value)} />
        </div>
      </div>

      <div className="mt-2">
        <TargetTimeFields value={targetTime} onChange={setTargetTime} />
      </div>

      <button type="submit" className="btn btn-primary btn-sm mt-3" disabled={busy || !title.trim()}>
        {busy ? t('common.saving') : t('detail.save')}
      </button>
    </form>
  );
}
